import React, { useState } from "react";
import { Lock } from "lucide-react";
import { toast } from "sonner";
import { useLanguage, useT } from "@/lib/LanguageContext";
import * as crmApi from "../../api/crmApi";
import { crmErrorMessage } from "../../api/crmErrors";
import { DELIVERY_KEYS, SESSION_STATUS_KEYS } from "../../contract";
import { fmtDate, fmtDayMonth } from "../../format";
import useClientTab from "../../hooks/useClientTab";
import useRole from "../../hooks/useRole";
import EmptyState from "../../ui/EmptyState";
import CrmLoadFailure from "../../ui/CrmLoadFailure";
import CrmButton from "../../ui/CrmButton";
import CrmInput from "../../ui/CrmInput";
import CrmAlertDialog from "../../ui/CrmAlertDialog";
import CrmPager from "../../ui/CrmPager";
import { cn } from "../../../../lib/utils";
import { FOCUS_RING } from "../../ui/classes";
import { CRM } from "../../../../constants/testIds/crm";

/**
 * "Void this pack" (C2.03 `POST …/packs/{balance_id}/void`, owner only): a correction for
 * a wrongly-assigned balance, not a refund path — it never touches Stripe, the ledger or
 * commission (offline assignment doctrine). Confirmed, with an optional note logged
 * alongside the void the same way an Adjust note is.
 */
function VoidPackButton({ clientId, pack, onChanged }) {
  const t = useT("crm");
  const tc = useT("common");
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const confirm = async () => {
    setBusy(true);
    try {
      await crmApi.voidPack(clientId, pack.balance_id, note.trim() ? { note: note.trim() } : {});
      toast.success(t("sessions.voidDone"));
      setOpen(false);
      setNote("");
      onChanged?.();
    } catch (e) {
      toast.error(crmErrorMessage(e, t, tc));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <CrmButton variant="link" size="sm" onClick={() => setOpen(true)} data-testid={CRM.clientPackVoid}>
        {t("sessions.void")}
      </CrmButton>
      <CrmAlertDialog
        open={open}
        onOpenChange={(o) => { setOpen(o); if (!o) setNote(""); }}
        title={t("sessions.voidTitle")}
        description={t("sessions.voidBody", { name: pack.pack_name, used: pack.sessions_used, total: pack.sessions_total })}
        confirmLabel={t("sessions.voidConfirm")}
        tone="danger"
        busy={busy}
        onConfirm={confirm}
        testId={CRM.clientPackVoidDialog}
        confirmTestId={CRM.clientPackVoidConfirm}
      >
        <label htmlFor="crm-client-pack-void-note" className="block text-xs uppercase tracking-[0.18em] text-eh-muted mb-1.5">
          {t("sessions.voidNote")}
        </label>
        <CrmInput id="crm-client-pack-void-note" value={note} onChange={(e) => setNote(e.target.value)} data-testid={CRM.clientPackVoidNote} />
      </CrmAlertDialog>
    </>
  );
}

/**
 * Manual pack correction (spec: "Manual pack adjust (+/-) here for corrections, logged
 * with who and when"). Owner only — the server refuses a helper (O). The correction can
 * carry an optional note (C2.03 `adjust {delta, note?}`), cleared after each use.
 *
 * Wire sign: the server's own contract (packs.py `_apply`: `$inc sessions_used: -delta`)
 * moves `sessions_used` by `-delta`, NOT `+delta` — `delta=-1` consumes one session
 * (`sessions_used` UP), `delta=+1` gives one back (`sessions_used` DOWN). "+" here means
 * "one more session used" (the same direction as an automatic Mark-complete deduction),
 * so it must send `delta:-1`; "−" means "one fewer session used" (give one back), so it
 * must send `delta:1`.
 */
function PackAdjuster({ clientId, pack, onChanged }) {
  const t = useT("crm");
  const tc = useT("common");
  const { lang } = useLanguage();
  const last = pack.last_adjusted;
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const adjust = async (delta) => {
    setBusy(true);
    try {
      await crmApi.adjustPack(clientId, pack.balance_id, note.trim() ? { delta, note: note.trim() } : { delta });
      setNote("");
      onChanged?.();
    } catch (e) {
      toast.error(crmErrorMessage(e, t, tc));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-2 text-sm text-eh-ink">
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-semibold">{t("sessions.adjust")}</span>
        <CrmButton variant="outline" size="sm" aria-label={t("sessions.minus")} disabled={busy || pack.sessions_used <= 0}
          onClick={() => adjust(1)} data-testid={CRM.clientPackMinus}>−</CrmButton>
        <span>{t("sessions.position", { used: pack.sessions_used, total: pack.sessions_total })}</span>
        <CrmButton variant="outline" size="sm" aria-label={t("sessions.plus")} disabled={busy || pack.sessions_used >= pack.sessions_total}
          onClick={() => adjust(-1)} data-testid={CRM.clientPackPlus}>+</CrmButton>
        {last && <span className="text-eh-muted">{t("sessions.logged", { who: last.by, when: fmtDate(last.at, lang) })}</span>}
        <VoidPackButton clientId={clientId} pack={pack} onChanged={onChanged} />
      </div>
      <CrmInput value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("sessions.adjustNotePlaceholder")}
        aria-label={t("sessions.adjustNote")} className="max-w-xs" data-testid={CRM.clientPackAdjustNote} />
    </div>
  );
}

/**
 * Sessions: one row per session — date, type, mode, status, pack deduction and, for the
 * OWNER only, the note preview (a row expands to the full note). A helper sees no note
 * content at all, whatever the payload carries — the UI hides by role, the server by
 * projection — and the footer says so.
 */
export default function SessionsTab({ client, onChanged }) {
  const t = useT("crm");
  const { lang } = useLanguage();
  const { can } = useRole();
  const owner = can("viewSessionNotes");
  const list = useClientTab(client.id, crmApi.listSessions);
  const [open, setOpen] = useState(null);
  // The Sessions envelope's `pack` (contract v1.9: + `last_adjusted`) once read; the
  // record's active pack until then.
  const pack = list.envelope?.pack || client.summary?.active_pack || client.pack;

  return (
    <div className="space-y-3">
      {list.loadError && <CrmLoadFailure error={list.loadError} onRetry={list.reload} />}
      {list.loaded && !list.loadError && list.rows.length === 0 && <EmptyState title={t("sessions.empty")} />}
      <ul className="space-y-2">
        {list.rows.map((s) => {
          const preview = owner && s.has_note ? s.note_preview : null;
          const expanded = open === s.booking_id;
          return (
            <li key={s.booking_id} data-testid={CRM.clientSession(s.booking_id)} className="rounded-2xl bg-eh-cream px-4 py-3 text-sm text-eh-ink">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span>
                  {fmtDayMonth(s.slot_start, lang)} · {s.service_name} · {t(DELIVERY_KEYS[s.delivery_mode] || "enum.delivery.in_person")}
                  {s.pack_delta ? <span className="ml-2 text-eh-muted">{t("sessions.packDelta", { delta: s.pack_delta })}</span> : null}
                </span>
                <span>{SESSION_STATUS_KEYS[s.status] ? t(SESSION_STATUS_KEYS[s.status]) : ""}</span>
              </div>
              {preview && (
                <button type="button" onClick={() => setOpen(expanded ? null : s.booking_id)} aria-expanded={expanded}
                  className={cn("mt-2 block w-full rounded-lg text-left text-eh-muted", !expanded && "truncate", FOCUS_RING)}>
                  {preview}
                </button>
              )}
            </li>
          );
        })}
      </ul>
      {list.total > list.pageSize && <CrmPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} />}
      {!owner && list.rows.length > 0 && (
        <p data-testid={CRM.clientNotesHidden} className="flex items-center gap-2 text-sm text-eh-muted">
          <Lock size={14} aria-hidden="true" /> {t("sessions.hiddenForHelper")}
        </p>
      )}
      {can("managePacks") && pack && <PackAdjuster clientId={client.id} pack={pack} onChanged={() => { list.reload(); onChanged?.(); }} />}
    </div>
  );
}
